import { colors } from '../constants';
import { normalizeLifecycleStatus } from './queryListUtils';
import { LIFECYCLE_STATUS_STYLES } from '../constants/allQueriesPage';

/**
 * Get lifecycle status metadata (label and color)
 * @param {string} statusLabel - Status label
 * @returns {Object|null} Status metadata or null
 */
export const getLifecycleStatusMeta = (statusLabel) => {
  if (!statusLabel) return null;
  const key = normalizeLifecycleStatus(statusLabel);
  return LIFECYCLE_STATUS_STYLES[key] || null;
};

/**
 * Get priority color
 * @param {string} priority - Priority value
 * @returns {string} Color hex code
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case 'high':
      return colors.error;
    case 'medium':
      return colors.warning;
    case 'low':
      return colors.info;
    default:
      return colors.textSecondary;
  }
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 80) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
};

/**
 * Get latest response info from query
 * @param {Object} query - Query object
 * @returns {Object} Response info with message, respondedAt, responderName
 */
export const getLatestResponseInfo = (query) => {
  if (!query) {
    return { message: '', respondedAt: null, responderName: '' };
  }

  const latestResponse = query.latestResponse || {};
  const fallbackResponder = latestResponse.respondedBy || query.lastResponseBy;

  return {
    message: latestResponse.message || query.lastResponseMessage || '',
    respondedAt: latestResponse.respondedAt || query.lastResponseAt || null,
    responderName: fallbackResponder?.name || fallbackResponder?.fullName || '',
  };
};

/**
 * Normalize ID value
 * @param {*} value - ID value (string, object with _id/id, etc.)
 * @returns {string|null} Normalized ID or null
 */
export const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || value.id || null;
};

/**
 * Check if query can be escalated
 * @param {Object} query - Query object
 * @returns {boolean} True if query can be escalated
 */
export const canEscalate = (query) => {
  if (!query) return false;
  const labelStatus = normalizeLifecycleStatus(query.statusLabel);
  return labelStatus !== 'escalated' && labelStatus !== 'closed';
};
